import {
  ANNOTATION_COLORS,
  FONT_SIZES,
  STROKE_WIDTHS,
  type ImageAnnotation
} from "../../domain/imaging/image-annotation.js";

/**
 * RENDERIZAÇÃO DAS ANOTAÇÕES — de objetos para SVG, sem DOM.
 *
 * Uma única implementação serve aos dois consumos (comando, itens 18/32):
 *   • a sobreposição do editor, desenhada por cima da foto;
 *   • a imagem ACHATADA gravada como versão anotada e usada no PDF.
 *
 * Ser pura (string entra, string sai) é o que garante que o que o usuário
 * vê no editor é exatamente o que vai para o documento — não existe um
 * segundo desenhista com regras próprias. Também é o que torna o desenho
 * testável sem navegador.
 *
 * As coordenadas chegam normalizadas (0..1) e são multiplicadas pelas
 * dimensões reais de saída: o mesmo objeto desenha certo na miniatura, no
 * editor ampliado e na imagem em resolução plena.
 */

const round = (value: number): number => Math.round(value * 100) / 100;

/** Escapa texto para XML — uma legenda com "&" ou "<" não pode corromper o SVG. */
function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/**
 * Pontas da seta. A cabeça é um triângulo proporcional à espessura da
 * linha, girado na direção do traço — assim uma seta fina não ganha uma
 * cabeça gigante nem uma grossa uma cabeça invisível.
 */
export function arrowHeadPoints(
  x1: number, y1: number, x2: number, y2: number, stroke: number
): Array<{ x: number; y: number }> {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const size = Math.max(stroke * 4, 1);
  const spread = Math.PI / 7;
  return [
    { x: x2, y: y2 },
    { x: x2 - size * Math.cos(angle - spread), y: y2 - size * Math.sin(angle - spread) },
    { x: x2 - size * Math.cos(angle + spread), y: y2 - size * Math.sin(angle + spread) }
  ];
}

function renderOne(annotation: ImageAnnotation, width: number, height: number): string {
  const color = ANNOTATION_COLORS[annotation.color];
  // A espessura é fração da MENOR dimensão: numa foto muito larga, usar a
  // largura deixaria o traço desproporcional na vertical.
  const base = Math.min(width, height);

  if (annotation.type === "text") {
    const fontSize = FONT_SIZES[annotation.fontSize] * height;
    // Contorno escuro por baixo do texto: legenda clara sobre foto clara
    // ficaria ilegível sem isso, e o comando exige texto legível (item 37).
    const common = `x="${round(annotation.x * width)}" y="${round(annotation.y * height)}" font-family="Helvetica, Arial, sans-serif" font-size="${round(fontSize)}" font-weight="700"`;
    const label = escapeXml(annotation.text);
    return (
      `<text ${common} fill="none" stroke="#000000" stroke-width="${round(fontSize * 0.18)}" stroke-linejoin="round">${label}</text>` +
      `<text ${common} fill="${color}">${label}</text>`
    );
  }

  const stroke = Math.max(STROKE_WIDTHS[annotation.strokeWidth] * base, 1);
  const strokeAttrs = `stroke="${color}" stroke-width="${round(stroke)}" fill="none" stroke-linecap="round" stroke-linejoin="round"`;

  if (annotation.type === "arrow") {
    const x1 = annotation.x1 * width, y1 = annotation.y1 * height;
    const x2 = annotation.x2 * width, y2 = annotation.y2 * height;
    const head = arrowHeadPoints(x1, y1, x2, y2, stroke)
      .map(point => `${round(point.x)},${round(point.y)}`)
      .join(" ");
    return (
      `<line x1="${round(x1)}" y1="${round(y1)}" x2="${round(x2)}" y2="${round(y2)}" ${strokeAttrs}/>` +
      `<polygon points="${head}" fill="${color}"/>`
    );
  }

  if (annotation.type === "ellipse") {
    // Só contorno, nunca preenchida (item 5) — preencher esconderia
    // justamente a região que a marcação quer evidenciar.
    const rx = (annotation.width * width) / 2;
    const ry = (annotation.height * height) / 2;
    return `<ellipse cx="${round(annotation.x * width + rx)}" cy="${round(annotation.y * height + ry)}" rx="${round(rx)}" ry="${round(ry)}" ${strokeAttrs}/>`;
  }

  return `<rect x="${round(annotation.x * width)}" y="${round(annotation.y * height)}" width="${round(annotation.width * width)}" height="${round(annotation.height * height)}" ${strokeAttrs}/>`;
}

/** Só os elementos, sem o `<svg>` — para embutir numa sobreposição já existente. */
export function annotationsToSvgElements(
  annotations: readonly ImageAnnotation[], width: number, height: number
): string {
  return annotations.map(annotation => renderOne(annotation, width, height)).join("");
}

/** SVG completo e autossuficiente, no tamanho real de saída. */
export function annotationsToSvg(
  annotations: readonly ImageAnnotation[], width: number, height: number
): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${round(width)}" height="${round(height)}" viewBox="0 0 ${round(width)} ${round(height)}">` +
    annotationsToSvgElements(annotations, width, height) +
    `</svg>`
  );
}
